import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box, useTheme, IconButton, Dialog, Slide, DialogTitle,
  Chip, Tooltip, TextField, InputAdornment, Typography,
  Paper, Stack, Collapse, Button,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import ModeEditOutlineIcon from "@mui/icons-material/ModeEditOutline";
import SearchIcon from "@mui/icons-material/Search";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import RouteIcon from "@mui/icons-material/Route";
import { GetAllRoutes } from "api/routes";
import { GetAllStations } from "api/stations";
import RouteForm from "./RouteForm";
import { BG_TINT } from "components/bento/bentoTokens";

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

const RouteCard = ({ route, onEdit, theme, stationMap }) => {
  const [expanded, setExpanded] = useState(false);
  const sorted = [...(route.navigator || [])].sort((a, b) => a.orderNum - b.orderNum);

  return (
    <Paper
      variant="outlined"
      sx={{ mb: 1.5, borderRadius: 2, overflow: "hidden", borderColor: route.validflag ? theme.palette.divider : theme.palette.error.light }}
    >
      <Box
        px={2} py={1.5}
        display="flex" alignItems="center" gap={1}
        sx={{ backgroundColor: BG_TINT, cursor: "pointer" }}
        onClick={() => setExpanded((p) => !p)}
      >
        <RouteIcon fontSize="small" color={route.validflag ? "secondary" : "disabled"} />
        <Typography variant="body1" fontWeight={600} flex={1}>{route.name}</Typography>
        <Chip label={route.id} size="small" variant="outlined" sx={{ fontSize: "0.7rem", height: 20 }} />
        {!route.validflag && <Chip label="Inactive" size="small" color="default" />}
        {route.isFanshine && <Chip label="Fanshine" size="small" color="warning" variant="outlined" />}
        <Chip label={`${sorted.length} stops`} size="small" color="secondary" variant="outlined" />
        <Tooltip title="Edit route">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(route); }} sx={{ "&:hover": { color: theme.palette.warning.main } }}>
            <ModeEditOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </Box>

      <Collapse in={expanded}>
        <Box px={2} py={1.5}>
          {route.description && <Typography variant="body2" color="text.secondary" mb={1}>{route.description}</Typography>}
          {sorted.length === 0 ? (
            <Typography variant="caption" color="text.secondary">No stations defined.</Typography>
          ) : (
            <Stack spacing={0}>
              {sorted.map((step, i) => (
                <Box key={step.station} display="flex" alignItems="center" gap={1}>
                  <Box sx={{ width: 24, textAlign: "center" }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>{step.orderNum}</Typography>
                  </Box>
                  <Box sx={{ width: 2, minHeight: 36, backgroundColor: i === sorted.length - 1 ? "transparent" : theme.palette.divider, mx: "auto" }} />
                  <Box flex={1} py={0.5}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography variant="body2" fontWeight={500}>{step.stationName}</Typography>
                      <Chip label={step.station} size="small" sx={{ height: 16, fontSize: "0.65rem" }} />
                      {stationMap[step.station]?.isSortStation && <Chip label="Sort" size="small" color="info" sx={{ height: 16, fontSize: "0.65rem" }} />}
                      {stationMap[step.station]?.finalStation && <Chip label="Final" size="small" color="success" sx={{ height: 16, fontSize: "0.65rem" }} />}
                    </Stack>
                    {step.status && <Typography variant="caption" color="text.secondary">Status: {step.status}</Typography>}
                  </Box>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

const RouteList = () => {
  const theme = useTheme();
  const [routes, setRoutes] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [editData, setEditData] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [stations, setStations] = useState([]);

  const stationMap = useMemo(
    () => Object.fromEntries(stations.map((s) => [s.id, s])),
    [stations]
  );

  const loadRoutes = useCallback(async () => {
    const res = await GetAllRoutes();
    if (res.success) { setRoutes(res.data); setFiltered(res.data); }
  }, []);

  useEffect(() => { loadRoutes(); }, [loadRoutes]);
  useEffect(() => {
    GetAllStations().then((res) => { if (res.success) setStations(res.data); });
  }, []);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(q ? routes.filter((r) => r.id?.toLowerCase().includes(q) || r.name?.toLowerCase().includes(q)) : routes);
  }, [search, routes]);

  const handleEdit = (row) => { setEditData(row); setDialogOpen(true); };
  const handleSaved = () => { setDialogOpen(false); loadRoutes(); };

  return (
    <Box>
      <Box mb={2} display="flex" alignItems="center" gap={2}>
        <TextField
          size="small" sx={{ maxWidth: 320 }} placeholder="Search routes..."
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>{filtered.length} route{filtered.length !== 1 ? "s" : ""}</Typography>
        <Button variant="contained" color="secondary" size="small" startIcon={<AddIcon />}
          onClick={() => { setEditData(null); setDialogOpen(true); }}>
          New Route
        </Button>
      </Box>

      <Box sx={{ maxHeight: "68vh", overflowY: "auto", pr: 0.5 }}>
        {filtered.map((route) => (
          <RouteCard key={route._id} route={route} onEdit={handleEdit} theme={theme} stationMap={stationMap} />
        ))}
        {filtered.length === 0 && (
          <Box textAlign="center" py={6} color="text.secondary">
            <RouteIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
            <Typography>No routes found.</Typography>
          </Box>
        )}
      </Box>

      <Dialog open={dialogOpen} TransitionComponent={Transition} maxWidth="md" fullWidth onClose={() => setDialogOpen(false)}
        PaperProps={{ style: { backgroundColor: theme.palette.mode === "dark" ? theme.palette.primary[700] : theme.palette.grey[100] } }}
      >
        <DialogTitle>
          Edit Route
          <IconButton onClick={() => setDialogOpen(false)} sx={{ position: "absolute", right: 8, top: 8 }}>
            <CloseOutlinedIcon />
          </IconButton>
        </DialogTitle>
        <Box px={3} pb={3}>
          <RouteForm editData={editData} onSaved={handleSaved} onCancel={() => setDialogOpen(false)} />
        </Box>
      </Dialog>
    </Box>
  );
};

export default RouteList;
